import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import { fetchCountriesFromCSV } from '../data/countries';
import { Country } from '../types';
import { theme } from '../styles/theme';
import { LoadingOverlay, Spinner } from '../styles';
import { useArticles } from '../contexts/ArticleContext';

// 世界地図のGeoJSONデータのURL
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface WorldMapProps {
  className?: string;
}

interface GeoFeature {
  properties: {
    name?: string;
    NAME?: string;
    iso_a2?: string;
    ISO_A2?: string;
    iso_a3?: string;
  };
  rsmKey: string;
  geometry: any;
}

const WorldMapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  margin-bottom: 4rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-bottom: 3rem;
    height: 400px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    height: 300px;
  }
`;

const Tooltip = styled.div<{ visible: boolean }>`
  position: fixed;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, -100%);
  margin-top: -8px;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.2s;
`;

const CountryStats = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 1rem;
  color: #666;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: 0.5rem;
    font-size: 0.875rem;
  }
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 1.5rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: 1rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 0.5rem;
    margin-top: 0.5rem;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

const LegendColor = styled.div<{ colorType: 'visited_with_article' | 'visited_no_article' | 'unvisited' }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${props => {
    switch (props.colorType) {
      case 'visited_with_article':
        return '#f39c12';
      case 'visited_no_article':
        return '#97c2e5';
      default:
        return '#e6f7ff';
    }
  }};
  border: 1px solid #CCCCCC;
`;

const WorldMap: React.FC<WorldMapProps> = ({ className }) => {
  const navigate = useNavigate();
  const { articles } = useArticles();
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= parseInt(theme.breakpoints.mobile));

  // ウィンドウサイズの変更を監視
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.mobile));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    const loadCountries = async () => {
      setLoading(true);
      try {
        const data = await fetchCountriesFromCSV();
        setCountries(data);
      } catch (error) {
        console.error('国データの読み込みに失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, []);

  // 訪問済み国名をSetで管理し、高速な検索を可能にする
  const visitedCountryNameSet = useMemo(() => {
    return new Set(countries.filter(country => country.status === 'visited').map(country => country.name));
  }, [countries]);

  // カントリーの情報を取得する関数
  const getCountryInfo = (countryName: string): Country | undefined => {
    return countries.find(country => country.name === countryName);
  };

  // 国のステータスを取得する関数
  const getCountryStatus = (countryName: string): string => {
    const countryInfo = getCountryInfo(countryName);
    return countryInfo?.status || 'unvisited';
  };

  // 国が訪問済みかどうかを判定する関数（国名で判定）
  const isVisited = (countryName: string): boolean => {
    const countryInfo = getCountryInfo(countryName);
    return countryInfo ? countryInfo.status === 'visited' : false;
  };

  // 国に記事があるかどうかを判定する関数
  const hasArticles = (countryName: string): boolean => {
    const countryInfo = getCountryInfo(countryName);
    if (!countryInfo) return false;

    // 英語名と日本語名の両方でタグをチェック
    const nameToCheck = [countryInfo.name, countryInfo.nameJa].filter(Boolean);
    
    // いずれかの名前がタグとして記事に含まれていればtrue
    return articles.some(article => {
      if (!article.tags) return false;
      return nameToCheck.some(name => name && article.tags!.includes(name));
    });
  };

  // 訪問済み国をカウントする
  const visitedCountCount = useMemo(() => {
    return countries.filter(country => country.status === 'visited').length;
  }, [countries]);

  const handleMouseEnter = (geo: GeoFeature, e: React.MouseEvent) => {
    const countryName = geo.properties.name || geo.properties.NAME || '';
    const countryInfo = getCountryInfo(countryName);
    const status = getCountryStatus(countryName);
    const visited = status === 'visited';
    let tooltipText = countryName;
    
    if (visited && countryInfo) {
      const region = countryInfo.region ? `（${countryInfo.region}）` : '';
      const hasArticle = hasArticles(countryName) ? '記事あり' : '記事なし';
      tooltipText = `${countryName} ${region} - 行った国 (${hasArticle})`;
    } else {
      tooltipText = `${countryName} - 未訪問`;
    }
    
    setTooltipContent(tooltipText);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
  };

  const handleClick = (geo: GeoFeature) => {
    const countryName = geo.properties.name || geo.properties.NAME || '';
    if (isVisited(countryName)) {
      const countryInfo = countries.find(country => country.name === countryName);
      if (countryInfo) {
        const countryNameForUrl = countryInfo.nameJa || countryInfo.name;
        // スクロール位置をトップに戻す
        window.scrollTo(0, 0);
        navigate(`/country/${encodeURIComponent(countryNameForUrl)}`);
      }
    }
  };

  return (
    <WorldMapContainer className={className}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 150 }}
        style={{ width: "100%", height: "100%", backgroundColor: "#f0f8ff" }}
      >
        <ZoomableGroup zoom={1.5} center={[60, 30]}>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo: GeoFeature) => {
                const countryName = geo.properties.name || geo.properties.NAME || '';
                const visited = isVisited(countryName);
                const hasArticle = hasArticles(countryName);
                
                let fillColor = "#cce5ff"; // デフォルト（未訪問）
                let hoverFillColor = "#b3d9f2";
                let cursor = "default";
                
                if (visited) {
                  if (hasArticle) {
                    fillColor = "#f39c12"; // 訪問済み（記事あり）- オレンジ
                    hoverFillColor = "#e67e22";
                  } else {
                    fillColor = "#97c2e5"; // 訪問済み（記事なし）
                    hoverFillColor = "#7fb1d6";
                  }
                  cursor = "pointer";
                }

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke="#a3c4dc"
                    strokeWidth={0.5}
                    style={{
                      default: {
                        outline: "none",
                        cursor: cursor,
                        filter: visited ? "drop-shadow(0 0 2px rgba(0, 0, 0, 0.3))" : "none"
                      },
                      hover: {
                        fill: hoverFillColor,
                        outline: "none",
                        stroke: "#7fb1d6",
                        strokeWidth: 0.75
                      },
                      pressed: {
                        outline: "none",
                        stroke: "#5d8cb3"
                      }
                    }}
                    onMouseEnter={(e) => handleMouseEnter(geo, e)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleClick(geo)}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {loading && (
        <LoadingOverlay>
          <Spinner />
          <div>地図データを読み込み中...</div>
        </LoadingOverlay>
      )}

      {tooltipVisible && (
        <Tooltip
          visible={tooltipVisible}
          style={{ left: tooltipPosition.x, top: tooltipPosition.y }}
        >
          {tooltipContent}
        </Tooltip>
      )}

      <Legend>
        <LegendItem>
          <LegendColor colorType="visited_with_article" />
          <span>行った国（記事あり）</span>
        </LegendItem>
        <LegendItem>
          <LegendColor colorType="visited_no_article" />
          <span>行った国（記事なし）</span>
        </LegendItem>
        <LegendItem>
          <LegendColor colorType="unvisited" />
          <span>未訪問</span>
        </LegendItem>
      </Legend>
    </WorldMapContainer>
  );
};

export default WorldMap;